import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { EwentsTooltipDirective } from '@ewentscompany/ewents-components';
import { EwentsLoadingComponent } from '../../shared/components/ewents-loading/ewents-loading.component';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import {
  NotificationHubService,
  type UserInboxNotification,
} from '../../settings/notification-hub.service';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    EwentsTooltipDirective,
    EwentsLoadingComponent,
  ],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private readonly hub = inject(NotificationHubService);
  private readonly router = inject(Router);
  private readonly host = inject(ElementRef<HTMLElement>);

  open = signal(false);
  loading = signal(false);
  items = signal<UserInboxNotification[]>([]);
  unreadCount = signal(0);

  private pollTimer: ReturnType<typeof setInterval> | null = null;

  async ngOnInit() {
    await this.refreshCount();
    // ponytail: light poll; upgrade to SSE/websocket if inbox volume grows
    this.pollTimer = setInterval(() => void this.refreshCount(), 60_000);
  }

  ngOnDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(ev.target as Node)) {
      this.open.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    this.open.set(false);
  }

  async toggle() {
    if (this.open()) {
      this.open.set(false);
      return;
    }
    this.open.set(true);
    await this.loadInbox();
  }

  async refreshCount() {
    try {
      const { count } = await firstValueFrom(this.hub.getInboxUnreadCount());
      this.unreadCount.set(count);
    } catch {
      // ignore — bell stays quiet if hub unavailable
    }
  }

  async loadInbox() {
    this.loading.set(true);
    try {
      const [items, { count }] = await Promise.all([
        firstValueFrom(this.hub.getInbox({ limit: 30 })),
        firstValueFrom(this.hub.getInboxUnreadCount()),
      ]);
      this.items.set(items);
      this.unreadCount.set(count);
    } catch {
      this.items.set([]);
    } finally {
      this.loading.set(false);
    }
  }

  async onItemClick(item: UserInboxNotification) {
    if (!item.readAt) {
      try {
        const updated = await firstValueFrom(this.hub.markInboxRead(item.id));
        this.items.update(list =>
          list.map(n =>
            n.id === item.id ? { ...n, readAt: updated.readAt ?? new Date().toISOString() } : n
          )
        );
        this.unreadCount.update(c => Math.max(0, c - 1));
      } catch {
        /* soft */
      }
    }
    this.open.set(false);
    const link = this.resolveLink(item);
    if (link) {
      void this.router.navigateByUrl(link);
    }
  }

  async onDelete(ev: Event, item: UserInboxNotification) {
    ev.stopPropagation();
    ev.preventDefault();
    try {
      await firstValueFrom(this.hub.deleteInbox(item.id));
      const wasUnread = !item.readAt;
      this.items.update(list => list.filter(n => n.id !== item.id));
      if (wasUnread) {
        this.unreadCount.update(c => Math.max(0, c - 1));
      }
    } catch {
      /* soft */
    }
  }

  async markAllRead() {
    try {
      await firstValueFrom(this.hub.markInboxAllRead());
      this.items.update(list =>
        list.map(n => ({ ...n, readAt: n.readAt ?? new Date().toISOString() }))
      );
      this.unreadCount.set(0);
    } catch {
      /* soft */
    }
  }

  badgeLabel(): string {
    const n = this.unreadCount();
    if (n <= 0) return '';
    return n > 99 ? '99+' : String(n);
  }

  relativeTime(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  private resolveLink(item: UserInboxNotification): string | null {
    if (item.link?.startsWith('/')) return item.link;
    if (item.entityType === 'contract' && item.entityId) {
      return `/contracts/${item.entityId}`;
    }
    return null;
  }
}
